import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ArrowRight,
  BookOpen,
  Check,
  Clock3,
  ExternalLink,
  FileText,
  Mail,
  PackageCheck,
  Paperclip,
  Send,
  ShieldCheck,
  Truck,
  Warehouse,
} from 'lucide-react';
import './styles.css';

const supportEmail = 'forgecart-theme@ademisler.com';

const supportReasons = [
  'Theme setup, navigation, sections, and footer configuration',
  'B2B product pages, variant options, carts, and quick order flows',
  'Collection filtering, search, account templates, and checkout handoff issues',
  'Bug reports with reproducible steps, affected URLs, screenshots, and theme version',
];

const responseItems = [
  { icon: Clock3, label: 'First reply', value: 'Within two business days' },
  { icon: ShieldCheck, label: 'Critical bugs', value: 'Handled with priority' },
  { icon: PackageCheck, label: 'Launch help', value: 'Practical setup guidance' },
];

const resourceLinks = [
  { label: 'Theme documentation', href: '#documentation', note: 'Setup, sections, B2B workflows, and Theme Store notes' },
  { label: 'Shopify demo store', href: 'https://forgecart-b2b-theme.myshopify.com', note: 'Reference storefront and QA environment' },
  { label: 'Shopify admin', href: 'https://admin.shopify.com/store/forgecart-b2b-theme', note: 'Store admin for theme and content setup' },
];

const documentationTopics = [
  {
    title: 'Install and launch',
    body: 'Publish the theme, review typography and color settings, connect main menu and footer links, then test product, collection, cart, and checkout entry points.',
  },
  {
    title: 'B2B workflows',
    body: 'Use quick order, request quote, saved lists, account dashboard, reorder, and invoice-style pages to model a realistic wholesale procurement journey.',
  },
  {
    title: 'Catalog setup',
    body: 'Add pack size, minimum order quantity, lead time, part number, and volume pricing metafields so product tables and cards feel complete.',
  },
  {
    title: 'Support policy',
    body: 'Theme setup questions and bug reports are covered. Custom apps, bespoke integrations, and data migration projects can be scoped separately.',
  },
];

const maxAttachmentBytes = 4 * 1024 * 1024;

const initialForm = {
  name: '',
  email: '',
  storeUrl: '',
  themeVersion: '',
  subject: '',
  priority: 'normal',
  screenshotUrl: '',
  description: '',
};

function App() {
  const [form, setForm] = useState(initialForm);
  const [attachment, setAttachment] = useState(null);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [scroll, setScroll] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScroll(max > 0 ? window.scrollY / max : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const heroStyle = useMemo(
    () => ({ '--hero-shift': `${Math.min(scroll * 30, 24)}px` }),
    [scroll],
  );

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateAttachment = (event) => {
    const file = event.target.files?.[0] || null;
    if (file && file.size > maxAttachmentBytes) {
      setAttachment(null);
      setStatus({ state: 'error', message: 'Please upload a file under 4 MB or add a link instead.' });
      event.target.value = '';
      return;
    }
    setAttachment(file);
    setStatus({ state: 'idle', message: '' });
  };

  const openEmailDraft = () => {
    const subject = `[ForgeCart Support] ${form.subject || 'Support request'}`;
    const body = [
      'Theme: ForgeCart',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Store URL: ${form.storeUrl}`,
      `Theme version: ${form.themeVersion || 'Not provided'}`,
      `Priority: ${form.priority}`,
      `Screenshot URL: ${form.screenshotUrl || 'Not provided'}`,
      `Attachment: ${attachment?.name || 'Not provided'}`,
      '',
      form.description,
    ].join('\n');

    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const readAttachment = () => new Promise((resolve, reject) => {
    if (!attachment) {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({
        name: attachment.name,
        type: attachment.type || 'application/octet-stream',
        size: attachment.size,
        content: result.includes(',') ? result.split(',').pop() : result,
      });
    };
    reader.onerror = () => reject(new Error('Attachment could not be read.'));
    reader.readAsDataURL(attachment);
  });

  const submitForm = async (event) => {
    event.preventDefault();
    setStatus({ state: 'loading', message: 'Sending your request...' });

    try {
      const response = await fetch('/api/support', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...form, attachment: await readAttachment() }),
      });
      const result = await response.json().catch(() => ({}));

      if (response.ok && result.delivered) {
        setForm(initialForm);
        setAttachment(null);
        event.currentTarget.reset();
        setStatus({ state: 'success', message: 'Thanks. Your request was sent and an email confirmation is on its way.' });
        return;
      }

      openEmailDraft();
      setStatus({
        state: response.ok ? 'success' : 'error',
        message: `Email delivery is being finalized. An email draft has opened for ${supportEmail}; attach your file before sending if needed.`,
      });
    } catch (error) {
      openEmailDraft();
      setStatus({ state: 'error', message: `We could not reach the form endpoint. An email draft has opened for ${supportEmail}.` });
    }
  };

  return (
    <main>
      <nav className="topbar" aria-label="Primary">
        <a className="brand" href="#top" aria-label="ForgeCart support home">
          <span>ForgeCart</span>
          <small>theme support</small>
        </a>
        <div className="nav-links">
          <a href="#support">Support</a>
          <a href="#resources">Resources</a>
          <a href="https://forgecart-b2b-theme.myshopify.com" target="_blank" rel="noreferrer">Demo</a>
        </div>
      </nav>

      <section id="top" className="hero" style={heroStyle}>
        <img src="/assets/fc-banner-homepage-procurement.jpg" alt="Procurement team reviewing B2B supply orders in ForgeCart" />
        <div className="hero-overlay" />
        <div className="hero-copy reveal">
          <p className="eyebrow">Shopify B2B theme support</p>
          <h1>ForgeCart</h1>
          <p className="hero-line">Support and launch guidance for merchants building fast wholesale ordering storefronts.</p>
          <div className="hero-actions">
            <a className="button primary" href="#support">Start a request <ArrowRight size={18} strokeWidth={1.8} /></a>
            <a className="button ghost" href="#resources">View resources <BookOpen size={18} strokeWidth={1.8} /></a>
          </div>
        </div>
      </section>

      <section className="intro section-pad">
        <div>
          <p className="eyebrow">Prepared for Theme Store review</p>
          <h2>B2B storefront support with the operational details included.</h2>
        </div>
        <p>
          ForgeCart support is built for real merchant launches: product data, quick order,
          account workflows, catalog navigation, and the cart interactions that keep procurement moving.
        </p>
      </section>

      <section id="documentation" className="documentation section-pad">
        <div className="documentation-heading">
          <p className="eyebrow">Documentation</p>
          <h2>Setup notes for a clean ForgeCart launch.</h2>
        </div>
        <div className="documentation-list">
          {documentationTopics.map((topic) => (
            <article className="doc-item" key={topic.title}>
              <span>{topic.title}</span>
              <p>{topic.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="feature-band">
        <figure className="feature-image sticky-visual">
          <img src="/assets/fc-banner-quick-order-workflow.jpg" alt="Quick order workflow for bulk B2B purchasing" />
        </figure>
        <div className="feature-copy">
          <p className="eyebrow">What to include</p>
          <h2>Send the details that let us reproduce the issue.</h2>
          <ul className="check-list">
            <li><Check size={18} /> Store URL and theme version</li>
            <li><Check size={18} /> Page URL, template, or section name</li>
            <li><Check size={18} /> Product, variant, SKU, or customer account state</li>
            <li><Check size={18} /> Screenshot or screen recording link when helpful</li>
          </ul>
        </div>
      </section>

      <section className="support-grid section-pad" aria-label="Support coverage">
        <div className="support-copy">
          <p className="eyebrow">Coverage</p>
          <h2>Built for storefront operations, not vague ticket loops.</h2>
          <div className="reason-list">
            {supportReasons.map((item) => (
              <div className="reason" key={item}>
                <Warehouse size={18} strokeWidth={1.6} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="metrics" aria-label="Response commitments">
          {responseItems.map(({ icon: Icon, label, value }) => (
            <div className="metric" key={label}>
              <Icon size={22} strokeWidth={1.5} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="media-row" aria-label="ForgeCart theme previews">
        <img src="/assets/fc-product-corrugated-box.jpg" alt="Corrugated shipping box product photography" />
        <img src="/assets/fc-banner-quote-volume-pricing.jpg" alt="Volume pricing and quote workflow preview" />
        <img src="/assets/fc-product-stretch-film.jpg" alt="Stretch film product photography" />
      </section>

      <section id="support" className="form-section section-pad">
        <div className="form-intro">
          <p className="eyebrow">Contact</p>
          <h2>Tell us what is happening.</h2>
          <p>
            Use this form for theme setup questions and bug reports. For custom development,
            app integration, or data migration requests, include the scope and launch date.
          </p>
          <a className="mail-link" href={`mailto:${supportEmail}`}><Mail size={18} /> {supportEmail}</a>
        </div>

        <form className="support-form" onSubmit={submitForm}>
          <div className="field-pair">
            <label>Name<input name="name" value={form.name} onChange={updateField} autoComplete="name" required /></label>
            <label>Email<input name="email" value={form.email} onChange={updateField} type="email" autoComplete="email" required /></label>
          </div>
          <div className="field-pair">
            <label>Store URL<input name="storeUrl" value={form.storeUrl} onChange={updateField} type="url" placeholder="https://store.myshopify.com" required /></label>
            <label>Theme version<input name="themeVersion" value={form.themeVersion} onChange={updateField} placeholder="1.0.0" /></label>
          </div>
          <div className="field-pair">
            <label>Subject<input name="subject" value={form.subject} onChange={updateField} required /></label>
            <label>Priority<select name="priority" value={form.priority} onChange={updateField}><option value="normal">Normal</option><option value="launch-blocking">Launch blocking</option><option value="critical-bug">Critical bug</option></select></label>
          </div>
          <label>Screenshot or screen recording link<input name="screenshotUrl" value={form.screenshotUrl} onChange={updateField} type="url" placeholder="https://..." /></label>
          <label>Screenshot upload<span className="file-field"><Paperclip size={18} strokeWidth={1.7} /><input name="attachment" onChange={updateAttachment} type="file" accept="image/*,.pdf,.mp4,.mov,.webm" /></span><small className="field-note">{attachment ? `${attachment.name} selected` : 'Attach an image, PDF, or short recording under 4 MB.'}</small></label>
          <label>Description<textarea name="description" value={form.description} onChange={updateField} required rows="7" /></label>
          <button className="button submit" type="submit" disabled={status.state === 'loading'}>{status.state === 'loading' ? 'Sending...' : 'Send request'}<Send size={18} strokeWidth={1.8} /></button>
          <p className={`form-status ${status.state}`} aria-live="polite">{status.message}</p>
        </form>
      </section>

      <section id="resources" className="resources section-pad">
        <div>
          <p className="eyebrow">Resources</p>
          <h2>Useful links for setup and review.</h2>
        </div>
        <div className="resource-list">
          {resourceLinks.map((link) => (
            <a href={link.href} key={link.label} target={link.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
              <FileText size={20} strokeWidth={1.5} />
              <span><strong>{link.label}</strong><small>{link.note}</small></span>
              <ExternalLink size={18} strokeWidth={1.5} />
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById('root')).render(<React.StrictMode><App /></React.StrictMode>);

export default App;
